import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  Users,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { Spinner } from "../../components/common/Spinner"
import { AttorneyControlsBar } from "../../components/controls/AttorneyControlBar"
import { PaginationControls } from "../../components/common/PaginationControls"
import AttorneyCard from "../../components/cards/AttorneyCard"
import { attorneysApi, Attorney } from "../../services/attorneysApi"
import { Toast } from "../../components/ui/toast"

export default function Attorneys() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [attorneys, setAttorneys] = useState<Attorney[]>([])
  
  // Search states - separate input value from actual search query
  const [searchInputValue, setSearchInputValue] = useState("")
  const [searchQuery, setSearchQuery] = useState("")

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(50)
  const [totalPages, setTotalPages] = useState<number>(0)

  // Delete modal states
  const [deleteAttorneyModal, setDeleteAttorneyModal] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [showToast, setShowToast] = useState(false)

  const fetchAttorneys = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await attorneysApi.getAttorneys(currentPage, recordsPerPage, searchQuery)
      
      setAttorneys(response.attorneys)
      // Ensure totalPages is at least 1, even when there are no attorneys
      setTotalPages(Math.max(1, response.total_pages))
    } catch (err) {
      console.error('Failed to fetch attorneys:', err)
      setError('Failed to load attorneys. Please try again.')
      // Fallback to empty state
      setAttorneys([])
      setTotalPages(1) // Set to 1 instead of 0 when there's an error
    } finally {
      setLoading(false)
    }
  }, [currentPage, recordsPerPage, searchQuery])

  useEffect(() => {
    fetchAttorneys()
  }, [fetchAttorneys])

  const handleRecordsPerPage = (value: string) => {
    const newValue = Number.parseInt(value)
    setRecordsPerPage(newValue)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const onAddHandle = () => {
    if (!loading) {
      navigate("/app/attorneys/add-attorney-modern")
    }
  }

  const updateAvailability = async (attorneyId: string, available: boolean) => {
    const previous = attorneys
    setAttorneys((current) =>
      current.map((attorney) => (attorney.id === attorneyId ? { ...attorney, available } : attorney))
    )
    try {
      await attorneysApi.updateAttorney(attorneyId, { available })
    } catch (err) {
      console.error("Failed to update availability:", err)
      setAttorneys(previous)
      setError("Failed to update availability. Please try again.")
    }
  }

  const selectAttorney = (attorneyId: string) => {
    navigate(`/app/attorneys/attorney-detail`, {
      state: { attorneyId, detail: true },
    })
  }

  const deleteAttorney = (deleteId: string) => {
    setDeleteAttorneyModal(true)
    setSelectedId(deleteId)
    setDeleteError(null)
  }

  const deleteAttorneyModalClose = () => {
    setDeleteAttorneyModal(false)
    setSelectedId("")
    setDeleteError(null)
    setDeleteLoading(false)
  }

  const deleteItem = async () => {
    try {
      // Validate selectedId before proceeding
      if (!selectedId || selectedId === "") {
        console.error('No attorney ID selected for deletion')
        deleteAttorneyModalClose()
        return
      }

      setDeleteLoading(true)
      setDeleteError(null)

      await attorneysApi.deleteAttorney(selectedId)
      
      // Close modal and refresh list
      deleteAttorneyModalClose()
      await fetchAttorneys() // Refresh the list
      
      // Show success toast
      setShowToast(true)
      
    } catch (err: any) {
      console.error('Failed to delete attorney:', err)
      setDeleteError(err.message || 'Failed to delete attorney. Please try again.')
      setDeleteLoading(false)
    }
  }

  // Handle search submission (when Enter is pressed or search icon is clicked)
  const handleSearchSubmit = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
  }

  // Handle search input change (just update the input value, don't trigger search)
  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value)
  }

  const recordsList = [
    { value: "10", label: "10 Records per page" },
    { value: "20", label: "20 Records per page" },
    { value: "30", label: "30 Records per page" },
    { value: "40", label: "40 Records per page" },
    { value: "50", label: "50 Records per page" },
    { value: "100", label: "100 Records per page" },
  ]

  return (
    <div className="min-h-screen bg-slate-50 pt-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Attorney Management</h1>
          <p className="text-slate-600">Manage and track your legal team efficiently</p>
        </div>

        {/* Controls Bar */}
        <AttorneyControlsBar
          recordsPerPage={recordsPerPage}
          onRecordsPerPageChange={handleRecordsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onAddAttorney={onAddHandle}
          recordsList={recordsList}
          searchQuery={searchInputValue}
          onSearchChange={handleSearchSubmit}
          onSearchInputChange={handleSearchInputChange}
          currentRecordsCount={attorneys.length}
        />

        {/* Attorneys Content */}
        <div className="space-y-2 md:space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : error ? (
            <Card className="border-red-200">
              <CardContent className="p-12 text-center">
                <div className="text-red-400 mb-4">
                  <Users className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-red-900 mb-2">Error loading attorneys</h3>
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={fetchAttorneys} className="bg-red-600 hover:bg-red-700 text-white">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          ) : attorneys.length > 0 ? (
            attorneys.map((attorney) => (
              <AttorneyCard
                key={attorney.id}
                attorney={attorney}
                onSelect={selectAttorney}
                onDelete={deleteAttorney}
                onAvailabilityChange={updateAvailability}
              />
            ))
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <div className="text-slate-400 mb-4">
                  <Users className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? 'No attorneys found matching your search' : 'No attorneys found'}
                </h3>
                <p className="text-slate-600 mb-4">
                  {searchQuery ? 'Try adjusting your search terms.' : 'Get started by adding your first attorney.'}
                </p>
                {!searchQuery && (
                  <Button onClick={onAddHandle} className="bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Attorney
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />

        <DeleteModal
          open={deleteAttorneyModal}
          onClose={deleteAttorneyModalClose}
          onConfirm={deleteItem}
          title="Delete Attorney"
          description="Are you sure you want to delete this attorney? This action cannot be undone."
          loading={deleteLoading}
          error={deleteError}
        />

        {/* Toast Notification */}
        <Toast
          message="Attorney deleted successfully!"
          type="success"
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      </div>
    </div>
  )
} 